import React, { useState } from 'react';
import { Edit2, Plus, Search, X } from 'lucide-react';
import Card from '../../../Components/Ui/Card';
import Table from '../../../Components/Ui/Table';
import Button from '../../../Components/Ui/Button';
import ActionDropdown from '../../../Components/Ui/ActionDropdown';
import { useNavigate } from 'react-router-dom';
import toastService from '../../../services/toastService';
import { useGetProductsQuery, useGetAllCategoriesQuery } from '../../../app/services/productApi';
import { useSelector } from 'react-redux';
import Loader from '../../../Components/Ui/Loader';

const ProductList = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const [searchQuery, setSearchQuery] = useState('');
  // const [selectedStatus, setSelectedStatus] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const { brandId } = useSelector((state) => state.myBrand);
  const { user } = useSelector((state) => state.userInfo);
  const { data, isLoading } = useGetProductsQuery(
    {
      id: brandId,
      search: searchQuery,
      // status: selectedStatus,
      category: selectedCategory,
      page: currentPage,
      limit: itemsPerPage,
    },
    { refetchOnMountOrArgChange: true },
  );
  const { data: categoriesData, isLoading: categoriesLoading } = useGetAllCategoriesQuery();
  const navigate = useNavigate();

  const productItems = data?.data || [];
  const pagination = data?.pagination || {};

  const handleAddProduct = () => {
    if (user?.status !== 'activate') {
      toastService.error(
        'Your brand account is pending verification. You cannot add products until approved by admin.',
      );
      return;
    }
    navigate('/brand/products/new');
  };

  const handleEditProduct = (id) => {
    navigate(`/brand/products/edit/${id}`);
  };

  const totalPages = pagination.totalPages || 1;

  // const statusOptions = [
  //   { value: '', label: 'All Statuses' },
  //   { value: 'active', label: 'Active' },
  //   { value: 'inactive', label: 'Inactive' },
  //   { value: 'draft', label: 'Draft' },
  //   { value: 'discontinued', label: 'Discontinued' },
  // ];

  if (isLoading) return <Loader />;

  return (
    <div className="space-y-6 px-4 md:px-0">
      <div className="flex flex-col sm:flex-row justify-center items-center sm:items-center sm:justify-between gap-3">
        <h2 className="text-lg xl:text-2xl font-bold md:pl-5">Product Management</h2>
        <div className="flex flex-wrap gap-2">
          <Button leftIcon={<Plus size={16} />} onClick={handleAddProduct} size="md">
            Add product
          </Button>
        </div>
      </div>

      <Card>
        <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-6 gap-4 flex-wrap">
          <div className="relative w-full md:w-72">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search"
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setCurrentPage(1);
              }}
              className="pl-9 pr-8 rounded-lg border border-gray-300 bg-white py-2 text-sm font-medium text-secondary hover:bg-gray-50 focus:outline-none focus:ring-2 border-color w-full"
            />
            {searchQuery && (
              <X
                className="absolute right-3 top-2.5 h-4 w-4 text-gray-500 cursor-pointer hover:text-secondary"
                onClick={() => setSearchQuery('')}
              />
            )}
          </div>

          <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
            {/* <select
              value={selectedStatus}
              onChange={(e) => {
                setSelectedStatus(e.target.value);
                setCurrentPage(1);
              }}
              className="border py-2 px-3 border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 border-color w-full sm:w-auto"
            >
              {statusOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select> */}
            {categoriesLoading ? (
              <div className="w-32">...Loading</div>
            ) : (
              <select
                className="border border-gray-300 rounded-lg text-sm border-color focus:border-[#832474] focus:outline-none focus:ring-2 p-2 w-full sm:w-auto"
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
              >
                <option value="">All Categories</option>
                {categoriesData?.data?.map((cat) => (
                  <option key={cat._id} value={cat._id}>
                    {cat.name}
                  </option>
                ))}
              </select>
            )}
          </div>
        </div>

        <Table
          columns={[
            {
              header: 'Name',
              accessor: (row) => (
                <button
                  onClick={() => navigate(`/brand/products/edit/${row._id}`)}
                  className="font-medium hover:text-primary overflow-hidden text-ellipsis whitespace-nowrap max-w-[200px]"
                  title={row.title}
                >
                  {row.title}
                </button>
              ),
            },
            {
              header: 'Category',
              accessor: (row) => row.category?.name || 'N/A',
            },
            // {
            //   header: 'SKU',
            //   accessor: (row) => (row.sku && row.sku.trim() !== '' ? row.sku : 'N/A'),
            // },
            {
              header: 'Product Code',
              accessor: (row) => row.productCode || 'N/A',
            },
            {
              header: 'MRP',
              accessor: (row) => (
                <span className="font-medium text-green-500">
                  {row.mrp ? `₹${Number(row.mrp).toFixed(2)}` : 'N/A'}
                </span>
              ),
            },
            {
              header: 'Tax (%)',
              accessor: (row) => (row.tax?.gstRate !== undefined ? `${row.tax.gstRate}` : 'N/A'),
            },
            {
              header: 'Actions',
              accessor: (row) => (
                <ActionDropdown
                  actions={[
                    {
                      label: 'Edit',
                      icon: Edit2,
                      onClick: () => handleEditProduct(row._id),
                    },
                  ]}
                />
              ),
            },
          ]}
          data={productItems}
          keyExtractor={(row) => row._id}
        />

        <div className="mt-4 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <p className="text-sm text-secondary">
            Showing {(currentPage - 1) * itemsPerPage + 1}-
            {Math.min(currentPage * itemsPerPage, productItems.length)} of{' '}
            {pagination.totalItems || productItems.length} items
          </p>

          <div className="flex flex-wrap items-center space-x-2">
            <button
              className="px-3 py-1 border border-gray-300 rounded-lg text-sm disabled:opacity-50"
              onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
            >
              Previous
            </button>
            {[...Array(totalPages)].map((_, i) => (
              <button
                key={i}
                className={`px-3 py-1 rounded-lg text-sm ${
                  currentPage === i + 1 ? 'bg-primary white-text' : 'border border-gray-300'
                }`}
                onClick={() => setCurrentPage(i + 1)}
              >
                {i + 1}
              </button>
            ))}
            <button
              className="px-3 py-1 border border-gray-300 rounded-lg text-sm disabled:opacity-50"
              onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages}
            >
              Next
            </button>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default ProductList;
